/* Phase 55.2 — Tiny helper to manage <head> SEO tags (OG, Twitter, canonical, robots).
 *
 * Pages register a list of [type, key, value] tuples and we ensure the
 * tags exist + are tagged with `data-seo-managed="1"` so they get cleaned
 * up cleanly on unmount/navigation.
 */
import { useEffect } from "react";

function upsertTag({ type, key, value, attr }) {
  if (value === undefined || value === null || value === "") return null;
  let sel;
  if (type === "meta-name") sel = `meta[name="${key}"]`;
  else if (type === "meta-prop") sel = `meta[property="${key}"]`;
  else if (type === "link") sel = `link[rel="${key}"]${attr ? `[${attr.k}="${attr.v}"]` : ""}`;
  else return null;
  let el = document.head.querySelector(sel);
  if (!el) {
    if (type === "link") {
      el = document.createElement("link");
      el.setAttribute("rel", key);
      if (attr) el.setAttribute(attr.k, attr.v);
    } else {
      el = document.createElement("meta");
      el.setAttribute(type === "meta-name" ? "name" : "property", key);
    }
    document.head.appendChild(el);
  }
  el.setAttribute(type === "link" ? "href" : "content", String(value));
  el.setAttribute("data-seo-managed", "1");
  return el;
}

/**
 * useSeoHead — declaratively manage page-level SEO tags.
 *
 * @param {Object} cfg
 * @param {string} [cfg.title]            document.title
 * @param {string} [cfg.description]      meta description (also og/twitter)
 * @param {string} [cfg.canonical]        absolute canonical URL
 * @param {string} [cfg.image]            absolute OG image URL
 * @param {string} [cfg.ogType]           og:type (default "website")
 * @param {Object} [cfg.product]          { price, priceCurrency, availability } for og:product:*
 * @param {Object} [cfg.hreflangs]        { de: url, fr: url, it: url, en: url, "x-default": url }
 */
export function useSeoHead(cfg) {
  useEffect(() => {
    if (!cfg) return;
    if (cfg.title) document.title = cfg.title;
    const desc = cfg.description;
    const created = [];
    const push = (t) => { if (t) created.push(t); };

    push(upsertTag({ type: "meta-name", key: "description", value: desc }));
    push(upsertTag({ type: "meta-prop", key: "og:type", value: cfg.ogType || "website" }));
    push(upsertTag({ type: "meta-prop", key: "og:site_name", value: "CarHive" }));
    push(upsertTag({ type: "meta-prop", key: "og:title", value: cfg.title }));
    push(upsertTag({ type: "meta-prop", key: "og:description", value: desc }));
    push(upsertTag({ type: "meta-prop", key: "og:url", value: cfg.canonical }));
    push(upsertTag({ type: "meta-prop", key: "og:image", value: cfg.image }));
    push(upsertTag({ type: "meta-prop", key: "og:image:alt", value: cfg.title }));
    push(upsertTag({ type: "meta-name", key: "twitter:card", value: cfg.image ? "summary_large_image" : "summary" }));
    push(upsertTag({ type: "meta-name", key: "twitter:title", value: cfg.title }));
    push(upsertTag({ type: "meta-name", key: "twitter:description", value: desc }));
    push(upsertTag({ type: "meta-name", key: "twitter:image", value: cfg.image }));

    if (cfg.product) {
      push(upsertTag({ type: "meta-prop", key: "product:price:amount", value: cfg.product.price }));
      push(upsertTag({ type: "meta-prop", key: "product:price:currency", value: cfg.product.priceCurrency || "CHF" }));
      push(upsertTag({ type: "meta-prop", key: "product:availability", value: cfg.product.availability || "in stock" }));
    }

    if (cfg.canonical) {
      push(upsertTag({ type: "link", key: "canonical", value: cfg.canonical }));
    }
    if (cfg.hreflangs) {
      Object.entries(cfg.hreflangs).forEach(([lc, url]) => {
        push(upsertTag({ type: "link", key: "alternate", value: url, attr: { k: "hreflang", v: lc } }));
      });
    }

    return () => {
      // Cleanup nodes we created/touched so navigation to a non-SEO page resets state.
      created.forEach((el) => {
        if (!el) return;
        // Don't tear down core static tags from index.html — only nodes we explicitly created.
        // We identified ours via data-seo-managed; if the tag pre-existed (no data-seo-managed
        // before this hook), upsertTag set it on us so we still own the cleanup.
        if (el.getAttribute && el.getAttribute("data-seo-managed") === "1" && el.parentNode) {
          el.parentNode.removeChild(el);
        }
      });
    };
  }, [JSON.stringify(cfg)]); // eslint-disable-line react-hooks/exhaustive-deps
}
